import React, { useRef } from "react";
import { TextField } from "@mui/material";
import { useSnackbar } from "../Utility/UserMessageContext";

/**
 * GenericTextField - A reusable text field component with database-safe character validation
 * 
 * @param {string} inputType - Validation type:
 *   - "general": Letters, numbers, spaces, dots, underscores, hyphens (default)
 *   - "name": Includes apostrophes and commas for names/addresses  
 *   - "email": Email-safe characters including @
 *   - "description": More permissive with common punctuation
 *   - "alphanumeric": Strict letters and numbers only
 * @param {boolean} allowInternational - Allow Unicode letters/numbers (default: true)
 * @param {boolean} unrestricted - Disable all validation (default: false)
 * @param {boolean} showValidationMessage - Show snackbar on invalid input (default: true)
 */

const GenericTextField = ({
  label,
  value,
  onChange,
  required = false,
  error = false,
  helperText,
  disabled = false,
  placeholder = "",
  maxLength = 255,
  inputType = "general", // "general", "name", "email", "description", "alphanumeric"
  allowInternational = true, // Allow Unicode letters and numbers
  unrestricted = false, // Disable all regex validation
  showValidationMessage = true, // Show notification on invalid input
  ...props
}) => {
  const { showSnackbar } = useSnackbar();
  const lastNotificationTime = useRef(0);
  // Get validation regex based on input type
  const getValidationRegex = () => {
    if (unrestricted) return null;
    
    const patterns = {
      // Basic safe characters for general use
      general: allowInternational 
        ? /^[\p{L}\p{N}\s._-]*$/u 
        : /^[a-zA-Z0-9\s._-]*$/,
      
      // For names and addresses (includes apostrophes, commas)
      name: /^[\p{L}\p{N}\s.',-]*$/u,
      
      // For email inputs (basic email-safe characters)
      email: /^[a-zA-Z0-9@._-]*$/,
      
      // For descriptions/comments (more permissive punctuation)
      description: /^[\p{L}\p{N}\s.,!?'":;()\-_]*$/u,

      number: /^[0-9]*$/,
      
      // Strict alphanumeric only
      alphanumeric: allowInternational 
        ? /^[\p{L}\p{N}]*$/u 
        : /^[a-zA-Z0-9]*$/,
    };
    
    return patterns[inputType] || patterns.general;
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    
    // If unrestricted mode, allow all characters
    if (unrestricted) {
      onChange(e);
      return;
    }
    
    const regex = getValidationRegex();
    if (!regex || inputValue === "" || regex.test(inputValue)) {
      onChange(e);
    } else {
      // Show notification for invalid characters (throttled to prevent spam)
      if (showValidationMessage) {
        const now = Date.now();
        if (now - lastNotificationTime.current > 2000) { // Throttle to max 1 per 2 seconds
          lastNotificationTime.current = now;
          
          const invalidCharMessage = getInvalidCharacterMessage();
          showSnackbar(invalidCharMessage, "warning");
        }
      }
    }
  };

  // Generate message about invalid characters based on input type
  const getInvalidCharacterMessage = () => {
    const messages = {
      general: "Invalid characters detected. Only letters, numbers, spaces, dots, underscores, and hyphens are allowed.",
      name: "Invalid characters detected. Only letters, numbers, spaces, apostrophes, commas, dots, and hyphens are allowed.",
      email: "Invalid characters detected. Only letters, numbers, @, dots, underscores, and hyphens are allowed.",
      description: "Invalid characters detected. Only letters, numbers, and common punctuation are allowed.",
      alphanumeric: "Invalid characters detected. Only letters and numbers are allowed.",
      number : "Invalid characters detected. Only numbers are allowed."

    };
    
    return messages[inputType] || messages.general;
  };

  // Generate helper text based on input type
  // const getHelperText = () => {
  //   if (unrestricted || helperText) return helperText;
    
  //   const helpTexts = {
  //     general: "Letters, numbers, spaces, dots, underscores, and hyphens only",
  //     name: "Letters, numbers, spaces, apostrophes, commas, dots, and hyphens only", 
  //     email: "Letters, numbers, @, dots, underscores, and hyphens only",
  //     description: "Letters, numbers, and common punctuation allowed",
  //     alphanumeric: "Letters and numbers only"
  //   };
    
  //   return helpTexts[inputType] || helpTexts.general;
  // };

  return (
    <TextField
      margin="dense"
      size="small"
      variant="outlined"
      label={label}
      value={value}
      onChange={handleInputChange}
      required={required}
      error={error}
      disabled={disabled}
      placeholder={placeholder}
      fullWidth
      inputProps={{ maxLength }}
      {...props}
      sx={{
        backgroundColor: "white", 
        borderRadius: "6px", 
        "& .MuiOutlinedInput-root": {
            "& fieldset": {
                borderColor: error ? "red" : "rgba(0, 0, 0, 0.23)",
            },
            "&:hover fieldset": {
                borderColor: error ? "red" : "rgba(0, 0, 0, 0.6)",
            },
            "&.Mui-focused fieldset": {
                borderColor: error ? "red" : "#06adaf",
            },
        },
        "& .MuiFormLabel-asterisk": {
            color: required ? "red" : "inherit",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#06adaf",
        },
        "& .MuiOutlinedInput-root.Mui-focused fieldset": {
            borderColor: "#06adaf",
        },
        ...props.sx
      }}
    />
  );
};

export default GenericTextField;